import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, Linkedin, MessageCircle } from 'lucide-react';
import { images } from '../data/mock';

const Team = () => {
  const { t, direction } = useLanguage();

  return (
    <section id="team" className="relative py-24 bg-white">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-amber-100 border border-amber-300 rounded-full text-amber-700 text-sm font-medium">
              {t('team')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t('teamTitle')}
          </h2>
          <p className="text-lg text-slate-600">
            {t('teamSubtitle')}
          </p>
        </div>

        {/* Team Member Card */}
        <div className="max-w-4xl mx-auto">
          <div className="group relative bg-white border-2 border-amber-200 rounded-3xl overflow-hidden hover:border-amber-400 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20">
            <div className="grid md:grid-cols-5 gap-8 p-8 md:p-12">
              {/* Image */}
              <div className="md:col-span-2 flex justify-center items-start">
                <div className="relative">
                  <div className="absolute -inset-3 bg-amber-200/40 rounded-2xl blur-2xl" />
                  <div className="relative rounded-2xl overflow-hidden border-2 border-amber-300 shadow-2xl w-64 h-64 md:w-full md:h-80 bg-white flex items-center justify-center p-4">
                    <img 
                      src={images.consultation}
                      alt="Fatima Abdulqudous Al Obeidli"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {/* Decorative corner */}
                  <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full opacity-20 blur-xl" />
                </div>
              </div>

              {/* Content */}
              <div className="md:col-span-3 space-y-6">
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">
                    {t('language') === 'ar' 
                      ? 'فاطمة عبدالقدوس العبيدلي' 
                      : 'Fatima Abdulqudous Al Obeidli'}
                  </h3>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1 bg-gradient-to-r from-amber-500 to-transparent" />
                    <p className="text-amber-600 font-semibold text-lg">
                      {t('teamRole')}
                    </p>
                    <div className="h-px flex-1 bg-gradient-to-l from-amber-500 to-transparent" />
                  </div>
                </div>

                <p className="text-slate-600 leading-relaxed text-lg">
                  {t('teamBio')}
                </p>

                {/* Expertise Tags */}
                <div className={`flex flex-wrap gap-3 ${direction === 'rtl' ? 'justify-end' : 'justify-start'}`}>
                  {[
                    t('service1Title'),
                    t('service2Title'),
                    t('service3Title'),
                    t('service4Title'),
                    t('service5Title'),
                    t('service6Title')
                  ].map((expertise, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-amber-100 border border-amber-300 rounded-full text-amber-700 text-sm font-medium hover:bg-amber-200 transition-colors"
                    >
                      {expertise}
                    </span>
                  ))}
                </div>

                {/* Contact Info */}
                <div className="pt-6 space-y-3">
                  <a href="tel:+971502060230" className="flex items-center gap-3 text-slate-600 hover:text-amber-600 transition-colors group/item">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center group-hover/item:bg-amber-200 transition-colors">
                      <Phone className="w-5 h-5" />
                    </div>
                    <span className="font-medium" dir="ltr">+971 50 206 0230</span>
                  </a>
                  <a href="https://wa.me/971502060230" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-600 hover:text-green-600 transition-colors group/item">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center group-hover/item:bg-green-200 transition-colors">
                      <MessageCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="font-medium" dir="ltr">+971 50 206 0230</span>
                  </a>
                  <a href="mailto:fatimaabdelqudous@gmail.com" className="flex items-center gap-3 text-slate-600 hover:text-amber-600 transition-colors group/item">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center group-hover/item:bg-amber-200 transition-colors">
                      <Mail className="w-5 h-5" />
                    </div>
                    <span className="font-medium" dir="ltr">fatimaabdelqudous@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Decorative gradient line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
          </div>
        </div>

        {/* Additional Team Info */}
        <div className="mt-16 text-center">
          <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {t('language') === 'ar'
              ? 'فريقنا من المحامين والمستشارين القانونيين ذوي الخبرة ملتزمون بتقديم أفضل الخدمات القانونية لعملائنا'
              : 'Our team of experienced lawyers and legal consultants are committed to providing the best legal services to our clients'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Team;
